import React, { useEffect, type KeyboardEvent } from 'react';
import { type UIAnalyticsEvent, useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { Text } from '@atlaskit/primitives';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useFlagService } from '@atlassian/jira-flags';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { useArchiveIssueUrl } from '@atlassian/jira-issue-archival-archives/src/controllers/useArchiveIssueUrl.tsx';
import { useFieldValue } from '@atlassian/jira-issue-field-base/src/services/field-value-service/index.tsx';
import {
	MountEvent,
	fireUIAnalytics,
	fireTrackAnalytics,
	fireScreenAnalytics,
	ContextualAnalyticsData,
	MODAL,
} from '@atlassian/jira-product-analytics-bridge';
import { ESC_KEY, ARCHIVE_ISSUE_MODAL_WIDTH_CATEGORY } from './constants.tsx';
import { useArchiveIssueModalActions } from './controllers/main.tsx';
import { useArchiveIssueModalStates } from './controllers/selectors.tsx';
import messages from './messages.tsx';
import { useArchiveIssue } from './services/index.tsx';
import { isIssueViewPage } from './utils.tsx';

export const Header = ({ title }: { title: string }) => <Heading size="medium">{title}</Heading>;
export const MainContent = ({ message }: { message: string }) => <Text as="p">{message}</Text>;
export const ArchiveModalButtons = ({ text }: { text: string }) => (
	<Text weight="medium" color="inherit">
		{text}
	</Text>
);

const ArchiveIssueModal = () => {
	const { formatMessage } = useIntl();
	const { showFlag } = useFlagService();
	const {
		isArchiveIssueModalOpen,
		issueKey,
		issueId,
		onCloseIssueViewModal,
		onIssueArchiveSuccess,
		triggerPointKey,
	} = useArchiveIssueModalStates();
	const { closeArchiveIssueModal } = useArchiveIssueModalActions();
	const archiveIssue = useArchiveIssue();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const issueKeyString: string = issueKey || '';
	const [issuetype] = useFieldValue({ issueKey: issueKeyString, fieldKey: 'issuetype' });
	const hierarchyLevel = issuetype?.hierarchyLevel || 0;

	const archiveIssueUrl = useArchiveIssueUrl();

	const onError = () => {
		showFlag({
			type: 'error',
			title: formatMessage(
				expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
					? messages.issueArchivalErrorFlagTitleIssueTermRefresh
					: messages.issueArchivalErrorFlagTitle,
				{
					issueKey,
				},
			),
			testId: 'jira-issue-archive.modal.error-flag',
		});
		fireTrackAnalytics(
			createAnalyticsEvent({
				action: 'archived',
				actionSubject: 'issue',
				attributes: { triggerPointKey },
			}),
			'archiveIssue Failed',
		);
		closeArchiveIssueModal();
	};

	const onSuccess = () => {
		fireTrackAnalytics(
			createAnalyticsEvent({
				action: 'archived',
				actionSubject: 'issue',
				attributes: { triggerPointKey },
			}),
			'archiveIssue Successful',
		);

		if (isIssueViewPage()) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.location.reload();
			return;
		}

		if (issueKey && issueId) {
			onIssueArchiveSuccess?.({ issueKey, issueId });
		}

		showFlag({
			type: 'success',
			title: formatMessage(
				expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
					? messages.issueArchivalSuccessFlagTitleIssueTermRefresh
					: messages.issueArchivalSuccessFlagTitle,
				{
					issueKey,
				},
			),
			testId: 'jira-issue-archive.modal.success-flag',
			actions: [
				{
					content: expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
						? messages.issueArchivalSuccessFlagActionViewIssueIssueTermRefresh
						: messages.issueArchivalSuccessFlagActionViewIssue,
					href: `/browse/${issueKey}`,
				},
				{
					content: expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
						? messages.issueArchivalSuccessFlagActionViewInArchiveIssueTermRefresh
						: messages.issueArchivalSuccessFlagActionViewInArchive,
					href: archiveIssueUrl,
				},
			],
		});
	};

	const onCloseHandler = (analyticsEvent: UIAnalyticsEvent) => {
		closeArchiveIssueModal();
		fireUIAnalytics(analyticsEvent, 'goBackArchiveIssueModalClick');
	};

	const onArchiveHandler = (analyticsEvent: UIAnalyticsEvent) => {
		archiveIssue(issueKey, onSuccess, onError);
		fireUIAnalytics(analyticsEvent, 'archiveArchiveIssueModalClick');
		onCloseIssueViewModal?.();
		closeArchiveIssueModal();
	};

	useEffect(() => {
		const preventKeyPropagationKeyDownHandler = (event: Event) => {
			if (isArchiveIssueModalOpen && event instanceof KeyboardEvent) {
				if (event.key === ESC_KEY) {
					event.preventDefault();
					closeArchiveIssueModal();
				}
				event.stopPropagation();
			}
		};

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		document.addEventListener('keydown', preventKeyPropagationKeyDownHandler, true);

		return () => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.removeEventListener('keydown', preventKeyPropagationKeyDownHandler, true);
		};
	}, [closeArchiveIssueModal, isArchiveIssueModalOpen]);

	let archiveIssueModalDescription;
	if (hierarchyLevel === 0) {
		archiveIssueModalDescription = expVal(
			'issue-terminology-refresh-m2-replace',
			'isEnabled',
			false,
		)
			? messages.archiveModalDescriptionIssueTermRefresh
			: messages.archiveModalDescription;
	} else {
		archiveIssueModalDescription = expVal(
			'issue-terminology-refresh-m2-replace',
			'isEnabled',
			false,
		)
			? messages.archiveModalDescriptionLOneIssueTermRefresh
			: messages.archiveModalDescriptionLOne;
	}

	return (
		<JSErrorBoundary
			id="jiraArchiveIssueModal"
			packageName="jiraIssueArchivalModal"
			fallback="flag"
		>
			<ModalTransition>
				{isArchiveIssueModalOpen && (
					<ContextualAnalyticsData
						sourceType={MODAL}
						sourceName="archiveIssue"
						attributes={{ triggerPointKey }}
					>
						<Modal
							onClose={closeArchiveIssueModal}
							width={ARCHIVE_ISSUE_MODAL_WIDTH_CATEGORY}
							testId="issue-archival-modal"
						>
							<MountEvent onMount={(analyticsEvent) => fireScreenAnalytics(analyticsEvent)} />
							<ModalHeader>
								<ModalTitle appearance="warning">
									<Header
										title={formatMessage(
											expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
												? messages.archiveModalTitleIssueTermRefresh
												: messages.archiveModalTitle,
										)}
									/>
								</ModalTitle>
							</ModalHeader>
							<ModalBody>
								<MainContent
									message={formatMessage(archiveIssueModalDescription, {
										b: (chunk: string): React.ReactNode => <Text as="strong">{chunk}</Text>,
										br: () => <br />,
									})}
								/>
							</ModalBody>
							<ModalFooter>
								<Button
									appearance="subtle"
									onClick={(event, analyticsEvent: UIAnalyticsEvent) => {
										onCloseHandler(analyticsEvent);
									}}
									testId="issue-archival-modal.go-back-button"
								>
									<ArchiveModalButtons text={formatMessage(messages.closeArchiveModalButton)} />
								</Button>
								<Button
									appearance="warning"
									onClick={(event, analyticsEvent: UIAnalyticsEvent) => {
										onArchiveHandler(analyticsEvent);
									}}
									testId="issue-archival-modal.restore-button"
								>
									<ArchiveModalButtons text={formatMessage(messages.issueArchiveButton)} />
								</Button>
							</ModalFooter>
						</Modal>
					</ContextualAnalyticsData>
				)}
			</ModalTransition>
		</JSErrorBoundary>
	);
};

export default ArchiveIssueModal;
